/**
 * StandardLoadingState Component
 *
 * Unified loading state component with multiple variants.
 * Used across all applications for consistent loading UX.
 *
 * @example
 * <StandardLoadingState variant="spinner" message="Loading data..." />
 */

import React from 'react';
import { Loader2 } from 'lucide-react';

export type LoadingVariant = 'spinner' | 'skeleton' | 'dots' | 'pulse';
export type LoadingSize = 'sm' | 'md' | 'lg';

export interface StandardLoadingStateProps {
  variant?: LoadingVariant;
  size?: LoadingSize;
  message?: string;
  fullScreen?: boolean;
}

export const StandardLoadingState: React.FC<StandardLoadingStateProps> = ({
  variant = 'spinner',
  size = 'md',
  message,
  fullScreen = false
}) => {
  // Size classes
  const sizeClasses = {
    sm: { icon: 20, text: 'text-sm' },
    md: { icon: 32, text: 'text-base' },
    lg: { icon: 48, text: 'text-lg' }
  };

  const renderLoadingVariant = () => {
    switch (variant) {
      case 'spinner':
        return (
          <div className="flex flex-col items-center justify-center gap-3">
            <Loader2
              className="animate-spin text-blue-600 dark:text-blue-400"
              size={sizeClasses[size].icon}
            />
            {message && (
              <p className={`${sizeClasses[size].text} text-gray-600 dark:text-gray-400`}>
                {message}
              </p>
            )}
          </div>
        );

      case 'skeleton':
        return (
          <div className="space-y-3 w-full">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-full" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-5/6" />
          </div>
        );

      case 'dots':
        return (
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="flex gap-2">
              <div className="w-3 h-3 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-3 h-3 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-3 h-3 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            {message && (
              <p className={`${sizeClasses[size].text} text-gray-600 dark:text-gray-400`}>
                {message}
              </p>
            )}
          </div>
        );

      case 'pulse':
        return (
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-900 rounded-full" />
              <div className="absolute inset-0 w-16 h-16 border-4 border-blue-600 dark:border-blue-400 rounded-full animate-ping" />
            </div>
            {message && (
              <p className={`${sizeClasses[size].text} text-gray-600 dark:text-gray-400`}>
                {message}
              </p>
            )}
          </div>
        );
    }
  };

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 bg-opacity-90 dark:bg-opacity-90 flex items-center justify-center z-50">
        {renderLoadingVariant()}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-12">
      {renderLoadingVariant()}
    </div>
  );
};
